/**
 * SearchField: a search box with a magnifier and a Clear button that appears once there is text (cockatrice-ui
 * `src/ui/SearchField.jsx`, ported to hyperscript; PLAN.md §0).
 * Escape clears it too. Without a visible label it is named "Search" (change with aria-label).
 * onChange(value) on every keystroke; onSearch(value) on Enter.
 */
import './css/search-field.css';
import { createEffect, createSignal, mergeProps, splitProps } from 'solid-js';
import { named, wrapControl } from './Field.ts';
import type { Without } from './Field.ts';
import Icon from './Icon.ts';
import { TextInput } from './TextField.ts';
import type { TextFieldProps } from './TextField.ts';
import { component, el, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';
import { uiText } from './text.ts';

export interface SearchFieldProps extends Attributes, Without<TextFieldProps, 'type' | 'iconStart' | 'end' | 'prefix' | 'suffix' | 'onChange'> {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called on every keystroke and on Clear (with ""). */
  readonly onChange?: ((value: string, event?: InputEvent) => void) | undefined;
  /** Called on Enter. */
  readonly onSearch?: ((value: string) => void) | undefined;
  /** Called after Clear or Escape. */
  readonly onClear?: (() => void) | undefined;
  /** The input's own keydown handler, run before the field's (Escape, Enter). */
  readonly onKeyDown?: ((event: KeyboardEvent) => void) | undefined;
  /** Placeholder; say what is searched. Default "Search". */
  readonly placeholder?: string | undefined;
  /** Name of the clear button. Default "Clear search". */
  readonly clearLabel?: string | undefined;
}

interface SearchInputProps extends SearchFieldProps {
  /** Whether the Field shows a visible label (then no aria-label of its own). */
  readonly hasLabel: boolean;
}

const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass', 'required'] as const;

export default function SearchField(props: SearchFieldProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () =>
    component(
      SearchInput,
      mergeProps(rest, {
        get hasLabel() {
          return Boolean(props.label);
        },
      }),
    ),
  );
}

function SearchInput(props: SearchInputProps): Child {
  const p = mergeProps(
    {
      get placeholder() {
        return uiText()('ui.search-field.placeholder');
      },
      get clearLabel() {
        return uiText()('ui.search-field.clear');
      },
      size: 'md',
    } as const,
    props,
  );
  const [local, rest] = splitProps(p, ['value', 'defaultValue', 'onChange', 'onSearch', 'onClear', 'clearLabel', 'hasLabel', 'onKeyDown', 'class']);
  const [val, setVal] = createSignal(local.value ?? local.defaultValue ?? '');
  createEffect(() => {
    if (local.value !== undefined) setVal(local.value);
  });
  // Set by the input's ref while the component is made, before any key or click can reach `clear`.
  let input: HTMLInputElement;

  const set = (v: string, event?: InputEvent): void => {
    setVal(v);
    local.onChange?.(v, event);
  };
  const clear = (): void => {
    input.value = '';
    set('');
    local.onClear?.();
    input.focus();
  };

  return component(
    TextInput,
    mergeProps(named(rest), {
      ref: (node: HTMLInputElement) => {
        input = node;
      },
      type: 'search',
      get class() {
        return ['ui-search-field', local.class].filter(Boolean).join(' ');
      },
      get 'aria-label'() {
        const own = rest['aria-label'];
        return local.hasLabel ? undefined : (own ?? uiText()('ui.search-field.label'));
      },
      iconStart: 'search' as const,
      enterkeyhint: 'search',
      autocomplete: 'off',
      get value() {
        return val();
      },
      onChange: set,
      onKeyDown: (event: On<KeyboardEvent, HTMLInputElement>) => {
        local.onKeyDown?.(event);
        if (event.key === 'Escape' && val() !== '') {
          event.preventDefault();
          clear();
        }
        if (event.key === 'Enter') local.onSearch?.(val());
      },
      get end() {
        return when(
          () => val() !== '',
          () =>
            el(
              'button',
              {
                type: 'button',
                class: 'ui-search-field__clear',
                'aria-label': () => local.clearLabel,
                title: () => local.clearLabel,
                onClick: clear,
              },
              component(Icon, { name: 'close', size: 'sm' }),
            ),
        );
      },
    }),
  );
}
